import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/setup';
import { useEffect, useRef, useMemo } from 'react';
import { getUserCompaniesAndRoles, getUserByToken } from '@/modules/auth/redux/AuthCRUD';
import * as auth from '@/modules/auth/redux/AuthRedux';
import { PermissionManager } from '@/__core/permissions/utils/PermissionManager';
import { getModulePermissions } from '@/__core/permissions/config/modulePermissions';
import { applyUserCompaniesAndRoles } from '@/__core/permissions/utils/applyUserCompaniesAndRoles';
import { mergeRolePermissions } from '@/__core/permissions/utils/permissionResolver';
import { getSelectedCompany } from '@/modules/auth/helpers/CompanyStorage';

/**
 * Custom hook để kiểm tra permissions của user hiện tại
 * Refactored to use PermissionManager class
 */
export const usePermissions = () => {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const user = useSelector((state: RootState) => state.auth.user);
  const globalRoles = useSelector((state: RootState) => state.auth.globalRoles);
  const hasGlobalRole = useSelector((state: RootState) => state.auth.hasGlobalRole);
  const dispatch = useDispatch();
  const isLoadingRef = useRef(false);
  const hasRunRef = useRef(false);

  // Create PermissionManager instance
  const permissionManager = useMemo(() => {
    if (!user || !selectedCompany) {
      return null;
    }
   
    const manager = new PermissionManager({
      user: {
        id: String(user.id),
        email: user.email || '',
        username: user.username || ''
      },
      globalRoles: globalRoles || [],
      hasGlobalRole,
      selectedCompany: {
        company_id: selectedCompany.company_id || '',
        company_name: selectedCompany.company_name || '',
        company_code: selectedCompany.company_code || '',
        company_type: selectedCompany.company_type || 'parent',
        roles: selectedCompany.roles || []
      }
    });
    
    return manager;
  }, [user, selectedCompany, globalRoles, hasGlobalRole]);


  // Load global roles và refresh company data nếu chưa có và user đã login
  // Also force refresh if user data seems stale (e.g., old role names)
  useEffect(() => {
    const loadUserData = async () => {
      // Trước tiên, kiểm tra xem có dữ liệu trong storage không
      const storedCompany = getSelectedCompany();
      const isStaleRoleData = (user as any)?.role === 'Admin' && (user as any)?.role_display_name === 'Admin';
      const shouldRefreshStaleRole = isStaleRoleData && !(window as any).__permissionsStaleRefreshDone;

      // Chỉ refresh nếu:
      // 1. Chưa có selectedCompany VÀ không có storedCompany HOẶC  
      // 2. User data seems stale
      const shouldRefresh = user && (
        (!selectedCompany && !storedCompany) ||
        // Force refresh if user role seems stale (e.g., "Admin" instead of "Admin XBox")
        shouldRefreshStaleRole
      );
      
      
      if (shouldRefresh && !isLoadingRef.current) {
        const existingPromise = (window as any).__permissionsUserDataFetchPromise as Promise<void> | undefined;
        if (existingPromise) {
          await existingPromise;
          return;
        }

        isLoadingRef.current = true;
        const fetchPromise = (async () => {
        try {
          
          // First, refresh the user profile data
          const profileResponse = await getUserByToken();
          dispatch(auth.actions.setUser(profileResponse.data));
          
          // Then, load companies and roles data
          const response = await getUserCompaniesAndRoles();
          const stored = getSelectedCompany();
          applyUserCompaniesAndRoles(dispatch, response.data, {
            syncSelectedCompanyId: selectedCompany?.company_id || stored?.company_id,
            autoSelectFirstCompany: !selectedCompany && !stored,
          });

          if (shouldRefreshStaleRole) {
            (window as any).__permissionsStaleRefreshDone = true;
          }
        } catch (error) {
          console.error('❌ Error loading user data:', error);
        }
        })();

        (window as any).__permissionsUserDataFetchPromise = fetchPromise;

        try {
          await fetchPromise;
        } finally {
          if ((window as any).__permissionsUserDataFetchPromise === fetchPromise) {
            delete (window as any).__permissionsUserDataFetchPromise;
          }
          isLoadingRef.current = false;
        }
      } else if (storedCompany && !selectedCompany) {
        // Nếu có dữ liệu trong storage nhưng chưa có trong Redux, load từ storage
        dispatch(auth.actions.setSelectedCompany(storedCompany));
      } 
    };

    loadUserData();
  }, [user?.id]);

  /**
   * Kiểm tra user có permission cụ thể không
   * @param resource - Tên resource (vd: 'users', 'roles', 'companies')
   * @param action - Hành động (vd: 'read', 'create', 'update', 'delete')
   * @returns boolean
   */
  const hasPermission = (resource: string, action: string): boolean => {
    if (!permissionManager) {
      return false;
    }
    
    const result = permissionManager.hasPermission(resource, action);
    return result;
  };

  /**
   * Kiểm tra user có bất kỳ permission nào trong danh sách không
   * @param permissions - Array of {resource, action} objects
   * @returns boolean
   */
  const hasAnyPermission = (permissions: Array<{resource: string, action: string}>): boolean => {
    if (!permissionManager) return false;
    return permissions.some(({ resource, action }) => permissionManager.hasPermission(resource, action));
  };

  /**
   * Kiểm tra user có tất cả permissions trong danh sách không
   * @param permissions - Array of {resource, action} objects
   * @returns boolean
   */
  const hasAllPermissions = (permissions: Array<{resource: string, action: string}>): boolean => {
    if (!permissionManager) return false;
    return permissions.every(({ resource, action }) => permissionManager.hasPermission(resource, action));
  };

  /**
   * Kiểm tra user có role cụ thể không
   * @param roleName - Tên role
   * @returns boolean
   */
  const hasRole = (roleName: string): boolean => {
    if (!permissionManager) return false;
    return permissionManager.hasRole(roleName);
  };

  /**
   * Kiểm tra user có bất kỳ role nào trong danh sách không
   * @param roleNames - Array of role names
   * @returns boolean
   */
  const hasAnyRole = (roleNames: string[]): boolean => {
    if (!permissionManager) return false;
    return roleNames.some(roleName => permissionManager.hasRole(roleName));
  };

  /**
   * Lấy danh sách tất cả permissions của user
   * @returns Object với resource làm key và array of actions làm value
   */
  const getAllPermissions = (): Record<string, string[]> => {
    if (!selectedCompany?.roles) return {};
    return mergeRolePermissions(selectedCompany.roles);
  };

  /**
   * Kiểm tra user có quyền truy cập module cụ thể không
   * @param module - Tên module (vd: 'users', 'finance', 'sales')
   * @returns boolean
   */
  const canAccessModule = (module: string): boolean => {
    if (!permissionManager) return false;
    
    // Get required permissions for the module from configuration
    const requiredPermissions = getModulePermissions(module);
    
    // Use PermissionManager to check access
    return permissionManager.canAccessModule(module, requiredPermissions);
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    getAllPermissions,
    canAccessModule,
    selectedCompany,
    user,
    permissionManager
  };
};

export default usePermissions;
